/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  Tabs,
  Upload,
  Button,
  message,
  Image,
  Steps,
  Switch,
  Checkbox,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import {
  addUserLocally,
  addPhotoToUser,
  fetchUsers,
} from "../../redux/user.slice";
import { fetchStores } from "../../redux/store.slice";
import { getBase64 } from "../../utils/files.helper";
import { photoService } from "../../services/photo.service";
import { useTranslation } from "react-i18next";

const { Option } = Select;
const { TabPane } = Tabs;

const UserFormModal = ({
  open,
  onCancel,
  onSubmit,
  isEdit,
  initialData,
  loading,
  isReadMode,
}) => {
  // FIXED: dispatch moved to TOP of component
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [previewImage, setPreviewImage] = useState("");
  const [storesOptions, setStoresOptions] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({});
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const { t } = useTranslation();
  const { stores } = useSelector((state) => state.store);
  const { company } = useSelector((state) => state.company);

  useEffect(() => {
    if (!stores.length) {
      dispatch(fetchStores());
    } else {
      const options = stores.map((s) => ({
        label: s.shopName,
        value: s.shopId,
      }));
      setStoresOptions(options);
    }
  }, [dispatch, stores]);

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file);
    }
    setPreviewImage(file.url || file.preview);
  };

  useEffect(() => {
    const loadExistingPhotos = () => {
      if (!initialData?.photos || initialData.photos.length === 0) {
        setExistingPhotos([]);
        return;
      }
      setExistingPhotos(initialData.photos);
    };

    if ((isEdit || isReadMode) && initialData) {
      const normalizedData = {
        ...initialData,
        isReceptionist: initialData?.isReceptionist ?? false,
      };

      setCurrentStep(0);
      setFormData(normalizedData);
      form.setFieldsValue(normalizedData);
      loadExistingPhotos();
    } else {
      setCurrentStep(0);
      setFormData({ isReceptionist: false });
      form.resetFields();
      form.setFieldsValue({ isReceptionist: false });
      setExistingPhotos([]);
    }
  }, [isEdit, isReadMode, initialData, form]);

  const closeModal = () => {
    setFile(null);
    setPreviewImage("");
    setExistingPhotos([]);
    setCurrentStep(0);
    setFormData({});
    form.resetFields();
    onCancel();
  };

  const handleOk = () => {
    const currentValues = form.getFieldsValue();
    const allValues = {
      ...formData,
      ...currentValues,
      isReceptionist:
        currentValues?.isReceptionist ?? formData?.isReceptionist ?? false,
    };

    const payload = {
      ...allValues,
      companyId: company?.companyId,
    };

    if (isEdit) {
      form.resetFields();
      setFormData({});
      onSubmit({
        ...initialData,
        ...allValues,
        companyId: company?.companyId,
        ...payload,
      });
    } else {
      form.resetFields();
      setFormData({});
      onSubmit(
        {
          ...allValues,
          companyId: company?.companyId,
          payload,
        },
        handleUpload,
      );
    }

    setFile(null);
    setPreviewImage("");
    setCurrentStep(0);
  };

  const handleUpload = async (user) => {
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);
    formDataUpload.append("userId", user ? user.id : initialData.id);
    formDataUpload.append("isPrincipal", "true");

    setUploading(true);

    try {
      // 1. Upload de la photo
      await photoService.uploadPhoto(formDataUpload);

      message.success(t("users.notifications.photoUploadSuccess"));

      // 2. Recharger la liste complète des utilisateurs depuis le serveur
      // Cela garantit que toutes les données (photos incluses) sont à jour
      await dispatch(fetchUsers());

      setFile(null);
      setPreviewImage("");
      setUploading(false);

      // Optionnel : fermer le modal après l'upload
      // closeModal();
    } catch (err) {
      console.error("Photo upload failed:", err);
      message.error(t("users.notifications.photoUploadError"));
      setUploading(false);
    }
  };

  const props = {
    onRemove: () => {
      setFile(null);
      setPreviewImage("");
    },
    beforeUpload: (file) => {
      setFile(file);
      handlePreview(file);
      return false;
    },
    fileList: [],
  };

  const validateStep1 = async () => {
    try {
      const fieldsToValidate = [
        "firstName",
        "lastName",
        "email",
        "phone",
        "login",
      ];
      if (!isEdit) {
        fieldsToValidate.push("password");
      }

      await form.validateFields(fieldsToValidate);

      if (!isEdit && !file && !previewImage) {
        message.warning("Il est recommandé d'ajouter une photo de profil");
      }

      return true;
    } catch (error) {
      return false;
    }
  };

  const handleNext = async () => {
    const isValid = await validateStep1();
    if (isValid) {
      const currentValues = form.getFieldsValue();
      setFormData((prev) => ({ ...prev, ...currentValues }));
      setCurrentStep(1);
    }
  };

  const handlePrevious = () => {
    const currentValues = form.getFieldsValue();
    setFormData((prev) => ({ ...prev, ...currentValues }));
    setCurrentStep(0);
  };

  useEffect(() => {
    if (Object.keys(formData).length > 0) {
      form.setFieldsValue(formData);
    }
  }, [currentStep, formData, form]);

  return (
    <Modal
      title={isEdit ? t("users.form.title") : t("users.form.titleAdd")}
      open={open}
      onCancel={closeModal}
      footer={null}
      destroyOnHidden
    >
      <Steps
        current={currentStep}
        style={{ marginBottom: 24 }}
        onChange={isReadMode ? (step) => setCurrentStep(step) : undefined}
        items={[
          { title: t("users.form.personalInfo") },
          { title: t("users.form.professionalInfo") },
        ]}
      />

      {currentStep === 0 && (
        <Form
          disabled={isReadMode}
          form={form}
          layout="vertical"
          name="user_form"
        >
          <Form.Item label="Photo">
            <div className="d-flex align-items-center gap-3 flex-wrap">
              <div>
                <Upload showUploadList={false} {...props}>
                  <div role="button" className="me-3">
                    <UploadOutlined />
                  </div>
                </Upload>
              </div>

              {previewImage && (
                <div>
                  <Image
                    style={{ maxWidth: 40, maxHeight: 40, borderRadius: "4px" }}
                    src={previewImage}
                    alt="New photo preview"
                  />
                </div>
              )}

              {isEdit && existingPhotos.length > 0 && (
                <div>
                  {/* Filtrer pour garder uniquement les photos utilisateur */}
                  {(() => {
                    const userPhotos = existingPhotos.filter((p) => p.userId);
                    const principalPhoto =
                      userPhotos.find((p) => p.isPrincipal) ||
                      userPhotos[userPhotos.length - 1];

                    return principalPhoto ? (
                      <div className="position-relative">
                        <Image
                          style={{
                            maxWidth: 40,
                            maxHeight: 40,
                            borderRadius: "4px",
                            border: "2px solid #52c41a",
                          }}
                          src={
                            principalPhoto.presignedUrl ||
                            principalPhoto.photoUrl
                          }
                          alt="User principal photo"
                        />
                        <div
                          style={{
                            position: "absolute",
                            top: "-5px",
                            right: "-5px",
                            background: "#52c41a",
                            color: "white",
                            borderRadius: "50%",
                            width: "14px",
                            height: "14px",
                            fontSize: "10px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                          }}
                          title="Photo principale"
                        >
                          ★
                        </div>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}

              {isEdit && (
                <Button
                  type="primary"
                  onClick={() => handleUpload()}
                  disabled={!file}
                  loading={uploading}
                  style={{ marginTop: file ? 16 : 0 }}
                >
                  {uploading
                    ? t("users.buttons.uploading")
                    : t("users.buttons.upload")}
                </Button>
              )}
            </div>
          </Form.Item>

          <Form.Item
            name="firstName"
            label={t("users.form.firstName")}
            rules={[
              {
                required: true,
                message: t("users.validations.firstNameRequired"),
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="lastName"
            label={t("users.form.lastName")}
            rules={[
              {
                required: true,
                message: t("users.validations.lastNameRequired"),
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="email"
            label={t("users.form.email")}
            rules={[
              { required: true, message: t("users.validations.emailRequired") },
              { type: "email", message: t("users.validations.emailInvalid") },
            ]}
          >
            <Input type="email" />
          </Form.Item>

          <Form.Item
            name="phone"
            label={t("users.form.phone")}
            rules={[
              { required: true, message: t("users.validations.phoneRequired") },
              {
                pattern: /^[+]?[0-9\s\-()]{8,15}$/,
                message: t("users.validations.phoneInvalid"),
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item name="login" label={t("users.form.login")}>
            <Input />
          </Form.Item>

          {!isEdit && (
            <Form.Item name="password" label={t("users.form.password")}>
              <Input.Password />
            </Form.Item>
          )}

          {!isReadMode && (
            <div className="d-flex flex-row-reverse gap-3">
              <Button type="primary" onClick={handleNext}>
                {t("common.next")}
              </Button>
              <Button className="secondary-button" onClick={closeModal}> {t("common.cancel")}</Button>
            </div>
          )}
        </Form>
      )}

      {currentStep === 1 && (
        <Form disabled={isReadMode} form={form} layout="vertical">
          <Form.Item
            name="roles"
            label={t("users.form.roles")}
            rules={[
              { required: true, message: t("users.validations.rolesRequired") },
            ]}
          >
            <Select mode="multiple" placeholder={t("users.form.roles")}>
              <Option value="ADMIN">{t("users.roles.ADMIN")}</Option>
              <Option value="MANAGER">{t("users.roles.MANAGER")}</Option>
              <Option value="PRODUCTION MANAGER">
                {t("users.roles.PRODUCTION MANAGER")}
              </Option>
              <Option value="ASSISTANT MANAGER">
                {t("users.roles.ASSISTANT MANAGER")}
              </Option>
              <Option value="SELLER">{t("users.roles.SELLER")}</Option>
            </Select>
          </Form.Item>

          <Form.Item name="companyPosition" label={t("users.form.position")}>
            <Input />
          </Form.Item>

          <Form.Item
            name="isReceptionist"
            valuePropName="checked"
            initialValue={false}
          >
            <Checkbox>{t("users.form.isReceptionist")}</Checkbox>
          </Form.Item>

          <Form.Item
            name="shopIds"
            label="Entité"
            rules={[
              { required: true, message: "Selectionnez au moins une entité!" },
            ]}
          >
            <Select mode="multiple" placeholder="Selectionner une entité">
              {storesOptions.map((s) => (
                <Option key={s.value} value={s.value}>
                  {s.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="userIsWaiter"
            label="Est un serveur"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          {!isReadMode && (
            <div className="d-flex flex-row-reverse gap-3">
              <Button
                loading={loading}
                type="primary"
                onClick={async () => {
                  try {
                    await form.validateFields(["roles", "shopIds"]);
                    handleOk();
                  } catch (error) {
                    console.log("Validation failed:", error);
                  }
                }}
              >
                {isEdit ? t("common.save") : t("users.buttons.create")}
              </Button>
              <Button onClick={handlePrevious}>{t("common.previous")}</Button>
              <Button className="secondary-button" onClick={closeModal}>{t("common.cancel")}</Button>
            </div>
          )}
        </Form>
      )}
    </Modal>
  );
};

UserFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isEdit: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  isReadMode: PropTypes.bool,
  initialData: PropTypes.shape({
    id: PropTypes.number,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    login: PropTypes.string,
    isReceptionist: PropTypes.bool,
    roles: PropTypes.arrayOf(PropTypes.string),
    companyPosition: PropTypes.string,
    shopIds: PropTypes.arrayOf(PropTypes.number),
    photos: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        photoUrl: PropTypes.string,
        presignedUrl: PropTypes.string,
        photoName: PropTypes.string,
        photoDescription: PropTypes.string,
        photoAltText: PropTypes.string,
        isPrincipal: PropTypes.bool,
        isActive: PropTypes.bool,
        userId: PropTypes.number,
        createdAt: PropTypes.string,
      }),
    ),
  }),
};

export default UserFormModal;
